import { Typography } from '@material-ui/core'
import React from 'react'

import { addCommaToPrice } from '../../helpers/numbers'
import { formatPromotionMinorAmount } from '../../helpers/promotionAmount'
import { getTranslate } from '../../localization'
import { Currency } from '../../types/currency'
import { useStyles } from './styles/index.styles'

/**
 * A row takes EITHER a major-unit number OR a minor-unit string — never both, never neither.
 *
 * The union is the point. Before it, `value: number` accepted both, and a caller passing
 * `Number(promotion.discountMinor)` compiled and rendered a USD discount a hundred times too
 * large on the final billing step. Nothing about the call site looked wrong: the field was a
 * number, the prop wanted a number.
 *
 * The two units cannot be told apart by inspection — 500 is a plausible amount either way —
 * so the distinction has to live in the type, where the compiler checks it, rather than in a
 * reviewer noticing that one particular field name ends in "Minor".
 */
type PriceRowAmount =
  /**
   * A major-unit figure computed by this app. `currency` is strict here because every such
   * value comes from our own pricing types, where a code outside the union is a bug.
   */
  | { value: number; currency: Currency; minorAmount?: never }
  /**
   * A minor-unit figure from the promotion service, with the currency IT reported.
   *
   * `currency` is a plain string here on purpose, and it is not laziness: the promotion
   * service can legitimately return a code this client's union does not list — a newly
   * supported currency, or a differently-cased one. Writing `as Currency` at the call site
   * would compile and would tell the compiler that case cannot arise, which is the opposite
   * of true; the compiler then stops asking anyone about it.
   *
   * The display consequence is deliberate. An unrecognised code renders as given and its
   * amount renders unscaled, because formatPromotionMinorAmount treats an unknown currency
   * as zero-minor-digit — better a whole number beside its own code than a decimal point
   * invented in the wrong place, or a bare figure with no unit at all. Narrowing to
   * `undefined` would produce that last one, which is the worst of the three.
   */
  | { minorAmount: string; currency: string; value?: never }

type PriceRowProps = {
  label: string
  translateLabel?: boolean
  isDiscount?: boolean
  bold?: boolean
} & PriceRowAmount

export const PriceRow: React.FC<PriceRowProps> = ({
  label,
  value,
  minorAmount,
  currency,
  translateLabel = true,
  isDiscount = false,
  bold
}) => {
  const classes = useStyles()

  // formatPromotionMinorAmount already applies the thousands separator, so the two branches
  // produce the same shape; only the scaling differs.
  const rendered =
    minorAmount !== undefined
      ? formatPromotionMinorAmount(minorAmount, currency ?? '')
      : String(addCommaToPrice(value ?? 0))

  return (
    <div className={classes.row}>
      <Typography
        variant="h6"
        className={[classes.mainText, bold ? classes.boldText : ''].join(' ')}>
        {translateLabel ? getTranslate(label) : label}
      </Typography>
      <Typography
        variant="h6"
        className={[
          classes.mainText,
          bold && classes.boldText,
          isDiscount && classes.greenText,
          classes.rightText
        ].join(' ')}>
        {`${rendered} ${getTranslate(currency || '')}`}
      </Typography>
    </div>
  )
}
